// client/src/app/routes.jsx
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useFeatureFlags } from "./providers/FeatureFlagsProvider";
import { useConfig } from "./providers/ConfigProvider.jsx";
import LoadingBlock from "@/shared/components/ui/LoadingBlock";

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

function Stub({ msg }) { return <div style={{ padding: 24 }}>{msg}</div>; }

const ReservationOverviewPanel = lazy(() => import("../features/reservations/List.jsx").catch(() => ({ default: () => <Stub msg="Reservations module not found." /> })));
const ReservationPrintOut = lazy(() => import("../features/reservations/Print.jsx").catch(() => ({ default: () => <Stub msg="Print module not found." /> })));
const TableView = lazy(() => import("../features/reservations/TableView.jsx").catch(() => ({ default: () => <Stub msg="Table View not found." /> })));
const MonthlySummaryCalendar = lazy(() => import("../features/reservations/Month.jsx").catch(() => ({ default: () => <Stub msg="Month View not found." /> })));
const BookingsOverview = lazy(() => import("../features/reservations/Overview.jsx").catch(() => ({ default: () => <Stub msg="Overview not found." /> })));
const ReservationsAuditLog = lazy(() => import("../features/reservations/AuditLog.jsx").catch(() => ({ default: () => <Stub msg="Audit Log not found." /> })));
const StaffTasksBoard = lazy(() => import("../features/staff/StaffTasksBoard.jsx").catch(() => ({ default: () => <Stub msg="Staff Tasks not enabled." /> })));
const KitchenBoard = lazy(() => import("../features/kitchen/KitchenBoard.jsx").catch(() => ({ default: () => <Stub msg="Kitchen Display not enabled." /> })));
const MenuBrowser = lazy(() => import("../features/menu/MenuBrowser.jsx").catch(() => ({ default: () => <Stub msg="Menu not enabled." /> })));
const SettingsPage = lazy(() => import("../features/settings/SettingsPage.jsx").catch(() => ({ default: () => <Stub msg="Settings module not found." /> })));
const OnboardingRouter = lazy(() => import("../features/onboarding/OnboardingRouter.jsx").catch(() => ({ default: () => <Stub msg="Setup wizard not found." /> })));
const HomePage = lazy(() => import("../features/home/HomePage.jsx").catch(() => ({ default: () => <Stub msg="Home page not found." /> })));
const UATPage = lazy(() => import("../pages/UATPage.jsx").catch(() => ({ default: () => <Stub msg="UAT page not found." /> })));
const UATWidgetPage = lazy(() => import("../pages/UATWidgetPage.jsx").catch(() => ({ default: () => <Stub msg="Widget UAT not found." /> })));

function AuthGuard({ children }) {
  return children;
}

export default function RoutesMap() {
  const { has } = useFeatureFlags();
  const { needsOnboarding } = useConfig();
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="enter"
        exit="exit"
      >
        <Suspense fallback={<div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}><LoadingBlock rows={8} hint="Loading page..." /></div>}>
          <Routes location={location}>
            <Route path="/uat" element={<UATPage />} />
            <Route path="/uat-widget" element={<UATWidgetPage />} />

            {/* All other routes wrapped in AuthGuard */}
            <Route path="/" element={<AuthGuard>{needsOnboarding ? <Navigate to="/onboarding" replace /> : <Navigate to="/home" replace />}</AuthGuard>} />
            <Route path="/home" element={<AuthGuard><HomePage /></AuthGuard>} />
            <Route path="/onboarding" element={<AuthGuard><OnboardingRouter /></AuthGuard>} />
            <Route path="/reservations" element={<AuthGuard><BookingsOverview /></AuthGuard>} />
            <Route path="/list" element={<AuthGuard><ReservationOverviewPanel /></AuthGuard>} />
            <Route path="/print" element={<AuthGuard><ReservationPrintOut /></AuthGuard>} />
            <Route path="/audit-log" element={<AuthGuard><ReservationsAuditLog /></AuthGuard>} />
            {has("tables") && <Route path="/tables" element={<AuthGuard><TableView /></AuthGuard>} />}
            {has("month") && <Route path="/month" element={<AuthGuard><MonthlySummaryCalendar /></AuthGuard>} />}
            {has("overview") && <Route path="/overview" element={<AuthGuard><BookingsOverview /></AuthGuard>} />}
            {has("staff") && <Route path="/staff" element={<AuthGuard><StaffTasksBoard /></AuthGuard>} />}
            {has("kds") && <Route path="/kitchen" element={<AuthGuard><KitchenBoard /></AuthGuard>} />}
            {has("menu") && <Route path="/menu" element={<AuthGuard><MenuBrowser /></AuthGuard>} />}
            {has("settings") && <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />}
            <Route path="*" element={<Stub msg="Not Found" />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}
